import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, linksApi } from '../api';

type Props = {
  itemId: number;
};

export default function ItemLinksSection({ itemId }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [url, setUrl] = useState('');
  const [label, setLabel] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: links = [] } = useQuery({
    queryKey: ['item-links', itemId],
    queryFn: () => linksApi.list(itemId),
  });

  const createMutation = useMutation({
    mutationFn: () => linksApi.create(itemId, { url, label: label || null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['item-links', itemId] });
      setUrl('');
      setLabel('');
      setShowForm(false);
      setError(null);
    },
    onError: (err) => {
      setError(
        err instanceof ApiError ? t([`error.${err.message}`, 'error.UNKNOWN']) : t('error.UNKNOWN'),
      );
    },
  });

  const removeMutation = useMutation({
    mutationFn: (id: number) => linksApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['item-links', itemId] }),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    createMutation.mutate();
  }

  return (
    <div className="bg-white rounded-lg shadow p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700 uppercase">{t('links.title')}</h3>
        {!showForm && (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="text-sm text-slate-600 hover:text-slate-900 hover:underline"
          >
            {t('links.add')}
          </button>
        )}
      </div>

      {error && <div className="rounded bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>}

      {showForm && (
        <form onSubmit={handleSubmit} className="space-y-3 bg-slate-50 rounded p-3">
          <input
            type="url"
            required
            placeholder="https://..."
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="block w-full rounded border border-slate-300 px-3 py-2 text-sm"
          />
          <input
            type="text"
            placeholder={t('links.labelPlaceholder')}
            maxLength={200}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="block w-full rounded border border-slate-300 px-3 py-2 text-sm"
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                setUrl('');
                setLabel('');
              }}
              className="text-sm text-slate-600 hover:text-slate-900"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="bg-slate-900 text-white rounded px-3 py-1.5 text-sm hover:bg-slate-800 disabled:opacity-50"
            >
              {createMutation.isPending ? t('common.working') : t('common.save')}
            </button>
          </div>
        </form>
      )}

      {links.length === 0 && !showForm ? (
        <p className="text-sm text-slate-500">{t('links.empty')}</p>
      ) : (
        <ul className="space-y-1">
          {links.map((l) => (
            <li key={l.id} className="flex items-center justify-between gap-2 text-sm">
              <a
                href={l.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-700 hover:text-slate-900 hover:underline truncate flex-1"
              >
                {l.label || l.url}
              </a>
              <button
                type="button"
                onClick={() => {
                  if (confirm(t('links.confirmDelete'))) removeMutation.mutate(l.id);
                }}
                className="text-red-600 hover:text-red-800 text-xs"
              >
                {t('common.delete')}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
